import { computed, watch, toValue, type MaybeRefOrGetter } from 'vue'
import { getActivePinia } from 'pinia'
import { useRecentValuesStore } from '../stores/useRecentValuesStore'
import {
  loadRecentValuesFromLocalStorage,
  saveRecentValuesToLocalStorage,
} from '../utils/recent-values-local-storage'
import { recordList, removeListItem } from '../utils/recent-values-policy'
import type { RecentListItem } from '../utils/recent-values-types'

export type { RecentListItem }

const DEFAULT_MAX = 5

export type UseRecentValuesOptions = {
  maxItems?: number
  /**
   * When `true` (default), lists load/save under `storageKey` via localStorage.
   * When `false`, only the Pinia slice is updated (in-memory, still shared reactive).
   */
  persist?: boolean
}

/**
 * Composable: **policy** (prepend, dedupe, cap) + optional **localStorage** sync on top of the
 * **Pinia** store (`useRecentValuesStore`). Same `storageKey` → one shared reactive list across components.
 */
export function useRecentValues(
  storageKey: MaybeRefOrGetter<string>,
  options: UseRecentValuesOptions = {}
) {
  if (getActivePinia() == null) {
    throw new Error(
      '[@gpx-spa/ui] useRecentValues requires Pinia. Call app.use(createPinia()) before app.mount(...).'
    )
  }
  const store = useRecentValuesStore()
  const maxItems = options.maxItems ?? DEFAULT_MAX
  const persist = options.persist ?? true

  const items = computed(() => {
    const k = toValue(storageKey)
    if (!k) return []
    return store.getList(k)
  })

  function resolveKey(): string {
    return toValue(storageKey)
  }

  function shouldPersist(): boolean {
    return persist && Boolean(resolveKey())
  }

  function persistToStorage(key: string, next: RecentListItem[]): void {
    if (!shouldPersist()) return
    saveRecentValuesToLocalStorage(key, next)
  }

  watch(
    () => toValue(storageKey),
    (k) => {
      if (!k) return
      if (!persist) return
      store.replaceList(k, loadRecentValuesFromLocalStorage(k))
    },
    { immediate: true }
  )

  function record(value: RecentListItem): void {
    const k = resolveKey()
    if (!k) return
    const next = recordList(store.getList(k), value, maxItems)
    store.replaceList(k, next)
    persistToStorage(k, next)
  }

  function remove(value: RecentListItem): void {
    const k = resolveKey()
    if (!k) return
    const next = removeListItem(store.getList(k), value)
    store.replaceList(k, next)
    persistToStorage(k, next)
  }

  function clear(): void {
    const k = resolveKey()
    if (!k) return
    store.replaceList(k, [])
    persistToStorage(k, [])
  }

  return { items, record, remove, clear }
}
